/**
 * Anmeldung im Frontend (Spec 8.2a, 8.2b).
 *
 * Wer angemeldet ist und mit welcher Rolle, sagt immer die API über
 * `GET /auth/me`. Das Frontend hält kein Token: angemeldet wird mit Passwort
 * oder über „Mit Microsoft anmelden" (ein Link zur API, die den Rest mit
 * Microsoft ausmacht), und danach schickt der Browser das Sitzungs-Cookie von
 * selbst mit. Es ist `HttpOnly` und für JavaScript unsichtbar.
 *
 * **Rollen werden nie hier durchgesetzt**, nur ausgeblendet; die Entscheidung
 * trifft die API.
 */
export type Role = "Tips.Admin" | "Tips.Finance" | "Tips.ShiftLead" | "Tips.Viewer";

export type AuthMode = "local" | "dev";
export type Pending = "PASSWORD_CHANGE" | "TOTP_SETUP" | "TOTP_VERIFY";

export interface MeUser {
  id: string;
  username: string | null;
  displayName: string | null;
  roles: Role[];
  roleLabel: string | null;
  pending: Pending | null;
  /** `PASSWORD` oder `MICROSOFT`. */
  loginMethod: string | null;
  /** Hat einen Passwortzugang. Ohne ihn nur „Mit Microsoft anmelden“. */
  passwordAccess: boolean;
  totpEnabled: boolean;
  totpRequired: boolean;
}

export interface MeDto {
  mode: AuthMode;
  /** Geht „Passwort vergessen" per Mail? Sonst über die Administration. */
  mailReset?: boolean;
  /** Wird „Mit Microsoft anmelden" angeboten? */
  microsoft?: boolean;
  user: MeUser | null;
}

/** Nur zum Ausblenden, nie zum Absichern. */
export function hasRole(user: MeUser | null | undefined, ...allowed: Role[]): boolean {
  return allowed.some((r) => user?.roles.includes(r) ?? false);
}

export const canReview = (u: MeUser | null | undefined): boolean =>
  hasRole(u, "Tips.Admin", "Tips.Finance", "Tips.ShiftLead");
export const canWrite = (u: MeUser | null | undefined): boolean => hasRole(u, "Tips.Admin", "Tips.Finance");
export const isAdmin = (u: MeUser | null | undefined): boolean => hasRole(u, "Tips.Admin");
